import { ProductAssetClient } from './../../../../base/models/models.swagger.generated';

import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ActionCompleteEventArgs, AsyncSettingsModel, CancelEventArgs, FileInfo, RemovingEventArgs, SelectedEventArgs, Uploader, UploaderModule, UploadingEventArgs } from '@syncfusion/ej2-angular-inputs';
import { AuthService } from 'src/app/base/services/auth.service';
import { ToastService } from 'src/app/base/services/toast.service';
import { AssetConfigurationsService } from 'src/app/domain/assets/asset-configurations.service';
import { environment } from 'src/environments/environment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AssetStorageEntryLicense, AssetStorageUploadResultDTO, ImageQuality, AssetStorageClient, AssetDTOStargate, AssetType } from 'src/app/base/models/models.swagger.generated';
import { MessageDirective, MessageSeverity, MessageVariant } from 'src/app/directives/message.directive';
import { Subscription } from 'rxjs';
import { AssetStorageUploadEditorFormType } from '../../asset-storage/asset-storage-edit/shared/asset-storage-edit-detail-form/asset-storage-upload-editor-form-type';
import { AssetUploadEditorFormType } from '../asset-upload-editor/asset-upload-editor-form-type';

interface ChunkUploadInfo {
  id: string;
  entryId?: number;
  assetIds?: string;
}

@Component({
    selector: 'app-multi-upload-uploader',
    templateUrl: './multi-upload-uploader.component.html',
    styleUrl: './multi-upload-uploader.component.scss',
    imports: [UploaderModule, TranslateModule, MessageDirective]
})
export class MultiUploadUploaderComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input()
  public isAssetStorageMode: boolean;

  @Input()
  public uploadEditForms: FormGroup<AssetStorageUploadEditorFormType>[] | FormGroup<AssetUploadEditorFormType>[];

  //AssetStorage
  @Input()
  public uploadPath: string;

  //ProductAssets
  @Input()
  public productNumbers: string[];

  @Output()
  fileForEditSelected = new EventEmitter<string>();

  @Output()
  uploadSuccessful = new EventEmitter();

  @ViewChild('multiUploader')
  public multiUploader: Uploader;

  @ViewChild('dropArea', { static: true })
  dropArea: ElementRef;

  public asyncSettings: AsyncSettingsModel

  public get uploadedFiles(): FileInfo[] {
    return this.multiUploader?.getFilesData();
  };

  public isUploading: boolean = false;

  MessageVariant = MessageVariant;
  MessageSeverity = MessageSeverity;

  private deniedAssetStorageFileExtensions: string = '';
  private supportedProductAssetFileExtensions: string = '';

  // File list item that is currently selected
  private selectedItem: Element | null = null;
  private selectedIndex: number | null = null;
  private hasAutoSelected: boolean = false;
  private eventListeners: (() => void)[] = [];
  private formSubscriptions: Subscription[] = [];

  private _chunkUploads: ChunkUploadInfo[] = [];

  constructor(
    public toastService: ToastService,
    private authService: AuthService,
    private assetConfigurationsService: AssetConfigurationsService,
    private assetStorageClient: AssetStorageClient,
    private productAssetClient: ProductAssetClient,
    private fb: FormBuilder,
    private renderer: Renderer2
  ) {

  }

  public ngOnInit() {
    if (this.isAssetStorageMode === undefined || this.isAssetStorageMode === null) {
      throw new Error('isAssetStorageMode input is required');
    }

    this.setAsyncSettings();
    this.setDeniedAndAllowedFileExtensions();
  }

  ngAfterViewInit(): void {
    this.observeUploaderFileList();
  }

  ngOnDestroy(): void {
    this.cleanupEventListeners();
  }

  public upload() {
    this.isUploading = true;
    // Hide the clear button
    const clearButtons = this.dropArea.nativeElement.querySelectorAll('.e-file-clear-btn');
    clearButtons.forEach((btn: HTMLElement) => {
      this.renderer.addClass(btn, 'hide-clear-btn');
    });
    //Hide the file selection
    const fileSelection = this.dropArea.nativeElement.querySelector('.e-file-select-wrap');
    this.renderer.addClass(fileSelection, 'hide-clear-btn');

    this.multiUploader.upload();
    this.cleanupEventListeners();
    this.unsubscribeFromAllFormValidity();
    this.removeFormValidityIcon();
  }

  public onUploading(args: UploadingEventArgs): void {
    var form = this.uploadEditForms.find(form => form.controls.intermediateFileInfo.value.name === args.fileData.name);
    if (this.isAssetStorageUploadEditorForm(form)) {
      this.setAssetStorageFields(args, form);
    } else {
      this.setProductAssetFields(args, form);
    }
    args.currentRequest?.setRequestHeader('Authorization', 'Bearer ' + this.authService.getBearerToken());
  }

  public onChunkUploading(args: UploadingEventArgs): void {
    if (this.isAssetStorageMode) {
      args.customFormData?.push({ assetStorageEntryId: this._chunkUploads?.find((chunkUpload) => chunkUpload.id === args.fileData.id)?.entryId });
    } else {
      args.customFormData?.push({ assetIds: this._chunkUploads?.find((chunkUpload) => chunkUpload.id === args.fileData.id)?.assetIds });
      //Needed in backend to determine if entry is key, once all chunks have been uploaded
      var form = this.uploadEditForms.find(form => form.controls.intermediateFileInfo.value.name === args.fileData.name) as FormGroup<AssetUploadEditorFormType>;
      args.customFormData?.push({ isKey: form.controls.isKey.value });
    }
    args.currentRequest?.setRequestHeader('Authorization', 'Bearer ' + this.authService.getBearerToken());
  }

  public onChunkSuccess(args: any) {
    if (args.chunkIndex === 0) {
      if (this.isAssetStorageMode) {
        var asResponseContent = AssetStorageUploadResultDTO.fromJS(JSON.parse(args.event.target.response));
        this._chunkUploads.push({ id: args.file.id, entryId: asResponseContent.assetStorageEntryId });
      } else {
        var paResponseContentList = JSON.parse(args.event.target.response) as AssetDTOStargate[];
        const assetIds = (paResponseContentList as any).map(prc => prc.Id);
        const assetIdsString = assetIds.join(',');
        this._chunkUploads.push({ id: args.file.id, assetIds: assetIdsString });
      }
    }
    setTimeout(() => { this.authService.refreshTokenIfNecessary(null, false).subscribe() }, 0);
  }

  //Triggers after all the selected files has processed to upload successfully or failed to server.
  public onActionComplete(args: ActionCompleteEventArgs) {
    let successFiles = [];
    let errorCnt = 0;
    args.fileData?.forEach((fileDetail) => {
      if (fileDetail.statusCode === '2') {
        successFiles.push(fileDetail);
      } else {
        errorCnt++;
      }
    });
    this.toastService.toasts.set([]);
    successFiles.forEach((fileDetail) => {
      this.toastService.showSuccess('i18n.domain.assets.asset-storage.upload-success.header', 'i18n.domain.assets.asset-storage.upload-success.message', fileDetail.name);
      if (errorCnt) {
        this.multiUploader.remove(fileDetail);
      }
    });

    if (!errorCnt) {
      this.uploadSuccessful.emit();
    }
  }

  public onFailure(obj: any) {
    this.toastService.showError('i18n.errors.server-error.title', obj.e.target.response);
  }

  public onFilesSelected(args: SelectedEventArgs) {
    const files: FileInfo[] = args.filesData;
    if (!files) {
      return;
    }
    if (!this.hasForbiddenFileExtensions(files)) {
      // If all files are forbidden
      if (files.length === 0) {
        return;
      }
    }

    for (let file of files) {
      var newForm = this.createNewForm();
      newForm.controls.fileExtension.setValue(file.name.split('.').pop());
      newForm.controls.totalSize.setValue(file.size);
      newForm.controls.intermediateFileInfo.setValue(file);
      this.addNewFormToArray(newForm);
    }
  }

  public onCanceling(args: CancelEventArgs): void {
    const entryId = this._chunkUploads?.find((chunkUpload) => chunkUpload.id === args.fileData.id)?.entryId;
    if (entryId && this.isAssetStorageMode && typeof entryId === 'number') {
      this.assetStorageClient.removeUpload(entryId).subscribe();
    } else if (entryId && !this.isAssetStorageMode && typeof entryId === 'string') {
      this.productAssetClient.removeUpload(entryId).subscribe();
    }
  }

  public onRemoving(args: RemovingEventArgs): void {
    var files = args.filesData;

    for (let file of files) {
      var form = this.uploadEditForms.find(form => form.controls.intermediateFileInfo.value.id === file.id);
      var formIndex: number;

      if (this.isAssetStorageFormArray(this.uploadEditForms)) {
        if (this.isAssetStorageUploadEditorForm(form)) {
          formIndex = this.uploadEditForms.indexOf(form);
        } else {
          console.error('New form does not match the type of existing forms in the array.');
        }
      } else if (this.isProductAssetFormArray(this.uploadEditForms)) {
        if (this.isProductAssetUploadEditorForm(form)) {
          formIndex = this.uploadEditForms.indexOf(form);
        } else {
          console.error('New form does not match the type of existing forms in the array.');
        }
      } else {
        console.error('Unknown form type.');
      }

      this.removeFormFromArray(formIndex);

      if (this.selectedIndex != null && this.selectedIndex === formIndex) {
        this.selectedIndex = null;
        this.selectedItem = null;
        this.fileForEditSelected.emit(null);
      }

      if (this.uploadEditForms.length === 0) {
        this.hasAutoSelected = false;
      }
    }
    args.currentRequest?.setRequestHeader('Authorization', 'Bearer ' + this.authService.getBearerToken());

    //If all items have been deleted, close dialog
    if (this.uploadEditForms.length === 0) {
      this.uploadSuccessful.emit();
    }
  }

  public clearUploader() {
    //Cancle running uploads
    const files = this.multiUploader.getFilesData();
    this.multiUploader?.cancel(files);
    //Clear uploader
    this.multiUploader?.clearAll();
    this.clearUploaderVariables();
  }

  protected clearUploaderVariables() {
    this._chunkUploads = [];
    this.clearFormArray();
    this.selectedIndex = null;
    this.selectedItem = null;
    this.hasAutoSelected = false;

    this.isUploading = false;
    // Unhide the clear button
    const clearButtons = this.dropArea.nativeElement.querySelectorAll('.e-file-clear-btn');
    clearButtons.forEach((btn: HTMLElement) => {
      this.renderer.removeClass(btn, 'hide-clear-btn');
    });
    //Unhide the file selection
    const fileSelection = this.dropArea.nativeElement.querySelector('.e-file-select-wrap');
    this.renderer.removeClass(fileSelection, 'hide-clear-btn');

    this.fileForEditSelected.emit(null);
  }

  private observeUploaderFileList(): void {
    const uploaderElement = this.dropArea.nativeElement;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && !this.isUploading) {
          const addedOrRemovedLi = Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes)).some(node => node.nodeName === 'LI');
          if (addedOrRemovedLi) {
            this.addClickListenerToUploaderFileList();
          }
        }
      });
    });

    observer.observe(uploaderElement, { childList: true, subtree: true });
  }

  private addClickListenerToUploaderFileList(): void {
    const dropAreaElement = this.dropArea.nativeElement;
    const fileListItems = dropAreaElement.querySelectorAll('.e-upload-files .e-upload-file-list');
    this.cleanupEventListeners();
    this.removeFormValidityIcon();

    fileListItems.forEach((item, index )=> {
      this.handleFormValidityIcon(item, index);

      const clickListener = this.renderer.listen(item, 'click', (event) => {
        //Exclude control buttons from click event
        if ((event.target as HTMLElement).classList.contains('e-file-remove-btn') ||
          (event.target as HTMLElement).classList.contains('e-file-pause-btn') ||
          (event.target as HTMLElement).classList.contains('e-file-play-btn') ||
          (event.target as HTMLElement).classList.contains('e-file-abort-btn')) {
          return;
        }
        this.onFileListItemClick(item, index);
      });

      const mouseoverListener = this.renderer.listen(item, 'mouseover', () => {
        this.renderer.addClass(item, 'hover-animation');
      });
      const mouseoutListener = this.renderer.listen(item, 'mouseout', () => {
        this.renderer.removeClass(item, 'hover-animation');
      });

      // Track the event listeners for cleanup
      this.eventListeners.push(clickListener, mouseoverListener, mouseoutListener);
    });

      // Automatically select the first item if no item has been selected yet
    if (!this.hasAutoSelected && fileListItems.length > 0) {
      this.onFileListItemClick(fileListItems[0], 0);
      this.hasAutoSelected = true;
    }
  }

  private handleFormValidityIcon(item, index): void {
    // Add icon element for form validity
    const iconElement = document.createElement('span');
    iconElement.classList.add('form-validity-icon');
    item.appendChild(iconElement);

    // Update icon based on form validity
    const form = this.uploadEditForms[index];
    this.updateFormValidityIcon(iconElement, form);
  }

  private removeFormValidityIcon(): void {
    const dropAreaElement = this.dropArea.nativeElement;
    const existingIcons = dropAreaElement.querySelectorAll('.form-validity-icon');
    existingIcons.forEach(icon => icon.remove());
  }

  private updateFormValidityIcon(iconElement: HTMLElement, form: FormGroup<AssetStorageUploadEditorFormType> | FormGroup<AssetUploadEditorFormType>): void {
    if (form.valid) {
      iconElement.classList.add('icon-checkmark--outline');
      iconElement.classList.add('form-validity-icon-green');
      iconElement.classList.remove('icon-warning--alt');
      iconElement.classList.remove('form-validity-icon-yellow');
    } else {
      iconElement.classList.add('icon-warning--alt');
      iconElement.classList.add('form-validity-icon-yellow');
      iconElement.classList.remove('icon-checkmark--outline');
      iconElement.classList.remove('form-validity-icon-green');
    }
  }

  private onFileListItemClick(item: Element, index: number): void {
    // Remove the selected class from the previously selected item
    if (this.selectedItem) {
      this.renderer.removeClass(this.selectedItem, 'selected');
    }

    // Add the selected class to the clicked item
    this.renderer.addClass(item, 'selected');
    this.selectedItem = item;
    this.selectedIndex = index;

    const fileInfo = this.uploadedFiles[index];
    var form = this.uploadEditForms.find(form => form.controls.intermediateFileInfo.value.id === fileInfo.id);
    this.fileForEditSelected.emit(form.controls.intermediateFileInfo.value.id);
  }

  private cleanupEventListeners(): void {
    // Remove all tracked event listeners
    this.eventListeners.forEach(unlisten => unlisten());
    this.eventListeners = [];
  }

  private addNewFormToArray(newForm: FormGroup<AssetStorageUploadEditorFormType> | FormGroup<AssetUploadEditorFormType>): void {
    if (this.uploadEditForms.length === 0) {
      (this.uploadEditForms as any).push(newForm);
      this.subscribeToFormValidity(newForm, 0);
    } else if (this.isAssetStorageFormArray(this.uploadEditForms)) {
      if (this.isAssetStorageUploadEditorForm(newForm)) {
        this.uploadEditForms.push(newForm);
        this.subscribeToFormValidity(newForm, this.uploadEditForms.length - 1);
      } else {
        console.error('New form does not match the type of existing forms in the array.');
      }
    } else if (this.isProductAssetFormArray(this.uploadEditForms)) {
      if (this.isProductAssetUploadEditorForm(newForm)) {
        this.uploadEditForms.push(newForm);
        this.subscribeToFormValidity(newForm, this.uploadEditForms.length - 1);
      } else {
        console.error('New form does not match the type of existing forms in the array.');
      }
    } else {
      console.error('Unknown form type.');
    }
  }

  private removeFormFromArray(index: number): void {
    this.uploadEditForms.splice(index, 1);
    this.unsubscribeFromFormValidity(index);
  }

  private clearFormArray(): void {
    this.uploadEditForms.splice(0, this.uploadEditForms.length);
    this.unsubscribeFromAllFormValidity();
  }

  private subscribeToFormValidity(form: FormGroup, index: number): void {
    const subscription = form.statusChanges.subscribe(() => {
      const dropAreaElement = this.dropArea.nativeElement;
      const fileListItems = dropAreaElement.querySelectorAll('.e-upload-files .e-upload-file-list');
      const item = fileListItems[index];
      const iconElement = item.querySelector('.form-validity-icon');
      if (iconElement) {
        this.updateFormValidityIcon(iconElement, form);
      }
    });
    this.formSubscriptions.push(subscription);
  }

  private unsubscribeFromFormValidity(index: number): void {
    this.formSubscriptions[index]?.unsubscribe();
    this.formSubscriptions.splice(index, 1);
  }

  private unsubscribeFromAllFormValidity(): void {
    this.formSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.formSubscriptions = [];
  }

  private createNewForm(): FormGroup<AssetStorageUploadEditorFormType> | FormGroup<AssetUploadEditorFormType> {
    if (this.isAssetStorageMode) {
      return this.fb.group<AssetStorageUploadEditorFormType>({
        id: this.fb.control<number>(null),
        language: this.fb.control<string>(null, [Validators.required]),
        source: this.fb.control<string>(null),
        license: this.fb.control<AssetStorageEntryLicense>(null, [Validators.required]),
        imageQuality: this.fb.control<ImageQuality>(null),
        path: this.fb.control<string>(this.uploadPath),
        fileExtension: this.fb.control<string>(null),
        base64FileData: this.fb.control<string>(null),
        originalFileName: this.fb.control<string>({ value: null, disabled: true }),
        assignedAssetKeyWords: this.fb.control<number[]>(null),
        totalSize: this.fb.control<number>(null),
        isStaticFileName: this.fb.control<boolean>(false),
        fileLink: this.fb.control<string>(null),
        intermediateFileInfo: this.fb.control<FileInfo>(null)
      });
    }

    return this.fb.group<AssetUploadEditorFormType>({
      id: this.fb.control<number>(null),
      publicName: this.fb.control<string>(null, [Validators.required, Validators.pattern(/^[a-z0-9_-]+$/)]),
      originalFileName: this.fb.control<string>({ value: null, disabled: true }),
      fileExtension: this.fb.control<string>({ value: null, disabled: true }),
      fileLink: this.fb.control<string>(null),
      language: this.fb.control<string>(null, [Validators.required]),
      type: this.fb.control<AssetType>(null, [Validators.required]),
      isKey: this.fb.control<boolean>(false, [Validators.required]),
      shopIds: this.fb.control<string[]>(['LITTLEBIT'], [Validators.required]),
      base64FileData: this.fb.control<string>(null),
      totalSize: this.fb.control<number>(null),
      localFilePath: this.fb.control<string>({ value: null, disabled: true }),
      altText: this.fb.control<string>(null),
      replaceProductLogos: this.fb.control<boolean>(false),
      replaceCustomizedLogos: this.fb.control<boolean>(false),
      intermediateFileInfo: this.fb.control<any>(null)
    });
  }

  //Type guards
  private isAssetStorageUploadEditorForm(form: FormGroup<any>): form is FormGroup<AssetStorageUploadEditorFormType> {
    return 'license' in form.controls && 'imageQuality' in form.controls && 'source' in form.controls && 'path' in form.controls;
  }

  private isProductAssetUploadEditorForm(form: FormGroup<any>): form is FormGroup<AssetUploadEditorFormType> {
    return 'publicName' in form.controls && 'originalFileName' in form.controls && 'fileExtension' in form.controls;
  }

  private isAssetStorageFormArray(forms: any[]): forms is FormGroup<AssetStorageUploadEditorFormType>[] {
    return forms.length === 0 || forms[0].controls.hasOwnProperty('path');
  }

  private isProductAssetFormArray(forms: any[]): forms is FormGroup<AssetUploadEditorFormType>[] {
    return forms.length === 0 || forms[0].controls.hasOwnProperty('publicName');
  }
  //End of type guards

  private setAsyncSettings(): void {
    if(this.isAssetStorageMode) {
      this.asyncSettings = {
        saveUrl: environment.apiServerUrl + environment.assetStorageRoute + 'Upload',
        chunkSize: 1048576
      };
    } else {
      this.asyncSettings = {
        saveUrl: environment.apiServerUrl + environment.productAssetRoute + 'Upload',
        chunkSize: 1048576
      };
    }
  }

  private setDeniedAndAllowedFileExtensions(): void {
    const config = this.assetConfigurationsService.assetConfiguration;
    if (config) {
      this.deniedAssetStorageFileExtensions = config.forbiddenAssetFileExtensions.join(',');
      this.supportedProductAssetFileExtensions = ([
        ...(config.supportedAssetDocumentFileExtensions || []),
        ...(config.supportedAssetImageFileExtensions || []),
        ...(config.supportedAssetLogoFileExtensions || []),
        ...(config.supportedLandingPageFileExtensions || [])
      ]).join(',');
    }
  }

  private hasForbiddenFileExtensions(files: FileInfo[]): boolean {
    const deniedAssetStorageFileExtensions = this.deniedAssetStorageFileExtensions.replaceAll('.', '').split(',');
    const supportedProductAssetFileExtensions = this.supportedProductAssetFileExtensions.replaceAll('.', '').split(',');

    const isForbidden = (file: FileInfo) => {
      const fileType = file.type.toLowerCase();
      return this.isAssetStorageMode
        ? deniedAssetStorageFileExtensions.includes(fileType)
        : !supportedProductAssetFileExtensions.includes(fileType);
    };

    const forbiddenFiles = files.filter(isForbidden);

    if (forbiddenFiles.length > 0) {
      this.toastService.showError('i18n.domain.assets.product-assets.edit-asset.file-type-invalid', null);
      forbiddenFiles.forEach(file => files.splice(files.indexOf(file), 1));
      return true;
    }

    return false;
  }

  private setAssetStorageFields(args: UploadingEventArgs, form: FormGroup<AssetStorageUploadEditorFormType>): void {
    args.customFormData?.push({ path: form.controls.path.value });
    args.customFormData?.push({ languageCodeIso3: form.controls.language.value });
    args.customFormData?.push({ license: form.controls.license.value });
    args.customFormData?.push({ source: form.controls.source.value });
    args.customFormData?.push({ imageQuality: form.controls.imageQuality.value });
    args.customFormData?.push({ assignedAssetKeyWords: form.controls.assignedAssetKeyWords.value });
    args.customFormData?.push({ totalSize: form.controls.totalSize.value });
    args.customFormData?.push({ isStaticFileName: form.controls.isStaticFileName.value });
    args.customFormData?.push({ changedStaticFileName: form.controls.originalFileName.value });
  }

  private setProductAssetFields(args: UploadingEventArgs, form: FormGroup<AssetUploadEditorFormType>): void {
    const productNumbersString = this.productNumbers?.join(',') || '';

    args.customFormData?.push({ productNumbers: productNumbersString });
    args.customFormData?.push({ publicName: form.controls.publicName.value });
    args.customFormData?.push({ originalFileName: form.controls.originalFileName.value });
    args.customFormData?.push({ fileExtension: form.controls.fileExtension.value });
    args.customFormData?.push({ languageCodeIso3: form.controls.language.value });
    args.customFormData?.push({ assetType: form.controls.type.value });
    args.customFormData?.push({ isKey: form.controls.isKey.value });
    args.customFormData?.push({ shopIds: form.controls.shopIds.value });
    args.customFormData?.push({ altText: form.controls.altText.value });
  }
}
